import { z } from 'zod';
import type { ModuloLicitacao } from '../enums/dominios';
import type { TipoAlvo, TipoNota, Visibilidade } from '../enums/notas';
import type { AnexoDto } from './arquivos';
import type { PessoaResumoDto } from './licitacao';

// Contratos das notas, post-its e setas (RQ-NOT-001 a RQ-NOT-004, RQ-COL-008). Só uuids trafegam.

export type AlvoDto = { tipo: TipoAlvo; uuid: string };

/**
 * Módulos da licitação não têm registro próprio; como alvo de post-it e de seta usam um uuid fixo por módulo
 * (o mesmo em todas as licitações: o alvo vale dentro da licitação aberta).
 */
export const uuidDoModulo = (modulo: ModuloLicitacao) => `00000000-0000-4000-8000-${String(modulo).padStart(12, '0')}`;

export function moduloDoUuid(uuid: string): ModuloLicitacao | null {
  const [, numero] = /^00000000-0000-4000-8000-(\d{12})$/.exec(uuid) ?? [];
  return numero ? (Number(numero) as ModuloLicitacao) : null;
}

export type NotaDto = {
  uuid: string;
  tipo: TipoNota;
  visibilidade: Visibilidade;
  autor: PessoaResumoDto;
  texto: string;
  cor: string | null;
  x: number | null;
  y: number | null;
  largura: number | null;
  altura: number | null;
  rotacao: number;
  /** Alvo ao qual o post-it está preso; a posição é relativa a ele. */
  presoA: AlvoDto | null;
  compartilhadaCom: PessoaResumoDto[];
  /** Arquivos anexados à nota (enviados direto ao S3, RQ-ARQ-003). */
  anexos: AnexoDto[];
  podeEditar: boolean;
  /** Nota privada de outra pessoa, vista porque o gestor ou administrador a acompanha (só leitura). */
  acompanhada: boolean;
  /** Em nota privada ou compartilhada: quem acima do autor pode acompanhá-la (aviso ao autor). */
  vistaPorSuperiores: 'gestores' | 'administradores' | null;
  /** ISO 8601 (UTC). */
  criadoEm: string;
  versao: number;
};

export type ConectorDto = { uuid: string; origem: AlvoDto; destino: AlvoDto; rotulo: string | null; versao: number };

export type NotasQuadroDto = { notas: NotaDto[]; conectores: ConectorDto[] };

const coordenada = z.number().finite().min(-100000).max(100000);
const medida = z.number().finite().min(10).max(10000);
const cor = z.string().trim().max(20);
const esquemaAlvo = z.object({ tipo: z.number().int().min(1).max(7), uuid: z.string().uuid() });

/** Post-it, texto ou nota geral no quadro (com posição). */
export const esquemaCriarNotaQuadro = z.object({
  tipo: z.number().int().min(2).max(4),
  texto: z.string().max(20000).default(''),
  cor: cor.nullable().optional(),
  x: coordenada,
  y: coordenada,
  largura: medida.optional(),
  altura: medida.optional(),
  rotacao: z.number().finite().min(-360).max(360).optional(),
  presoA: esquemaAlvo.nullable().optional(),
});
export type CriarNotaQuadroEntrada = z.infer<typeof esquemaCriarNotaQuadro>;

/**
 * Na licitação: nota de lista (tipo 1, padrão; sempre pública, RQ-NOT-002) ou post-it/texto no quadro da
 * licitação (tipos 3 e 4, com posição; nasce privado e pode ser preso a módulos e itens).
 */
export const esquemaCriarNotaProcesso = z
  .object({
    tipo: z.union([z.literal(1), z.literal(3), z.literal(4)]).default(1),
    texto: z.string().trim().max(20000).default(''),
    cor: cor.nullable().optional(),
    x: coordenada.optional(),
    y: coordenada.optional(),
    largura: medida.optional(),
    altura: medida.optional(),
    presoA: esquemaAlvo.nullable().optional(),
  })
  .refine((n) => (n.tipo === 1 ? n.texto.length > 0 : n.x !== undefined && n.y !== undefined), {
    message: 'Nota de lista precisa de texto; post-it precisa de posição.',
  });
export type CriarNotaProcessoEntrada = z.infer<typeof esquemaCriarNotaProcesso>;

export const esquemaAlterarNota = z.object({
  versao: z.number().int().positive(),
  texto: z.string().max(20000).optional(),
  cor: cor.nullable().optional(),
  x: coordenada.optional(),
  y: coordenada.optional(),
  largura: medida.optional(),
  altura: medida.optional(),
  rotacao: z.number().finite().min(-360).max(360).optional(),
  presoA: esquemaAlvo.nullable().optional(),
  visibilidade: z.number().int().min(1).max(3).optional(),
  compartilharCom: z.array(z.string().uuid()).max(200).optional(),
});
export type AlterarNotaEntrada = z.infer<typeof esquemaAlterarNota>;

/** Quem a pessoa (gestor ou administrador) pode acompanhar e quem está marcado. */
export type AcompanhamentoDto = {
  pessoas: { uuid: string; nome: string; perfil: string; acompanhando: boolean }[];
};

/** Lista completa de quem acompanhar (substitui a anterior). */
export const esquemaAlterarAcompanhamento = z.object({ acompanhar: z.array(z.string().uuid()).max(500) });
export type AlterarAcompanhamentoEntrada = z.infer<typeof esquemaAlterarAcompanhamento>;

export const esquemaCriarConector = z.object({
  origem: esquemaAlvo,
  destino: esquemaAlvo,
  rotulo: z.string().trim().max(200).nullable().optional(),
});
export type CriarConectorEntrada = z.infer<typeof esquemaCriarConector>;
